import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import useItemService from "../../services/api/itemService";
import { enums } from "../../constants/enum";
import currencyFormatter from "../../util/currencyFormatter";

import { DeleteIcon, EditIcon, LibraryAddIcon } from "../../assets/icons";
import {
  Badge,
  Button,
  DataRow,
  IconButton,
  ListBox,
  Loading,
  PageTitle,
  Section,
} from "../../components";
import "./InventoryPage.scss";
import { useConfirm } from "../../hooks/confirm";

function ItemDetailsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { itemId } = useParams();
  const confirm = useConfirm();
  const { getItemById, queryKey, deleteItem } = useItemService();

  const mutation = useMutation({
    mutationFn: deleteItem,
    onSuccess: async (payload) => {
      toast.success(payload?.message);
      navigate("/inventory", { replace: true });
      queryClient.resetQueries({
        queryKey: [queryKey],
      });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
      );
    },
  });

  const {
    data: item,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [queryKey, itemId],
    queryFn: () => getItemById(itemId),
    enabled: !!itemId,
    retry: 1,
  });

  function makeBadgeVariant(itemCategory) {
    switch (itemCategory) {
      case enums.ITEM_CATEGORIES.FRAME:
        return "info";
      case enums.ITEM_CATEGORIES.LENSE:
        return "success";
      case enums.ITEM_CATEGORIES.ACCESSORIES:
        return "warning";
      case enums.ITEM_CATEGORIES.SUNGLASS:
        return "danger";
      default:
        return "info";
    }
  }

  async function handleDelete() {
    const isConfirmed = await confirm({
      title: "Delete Item?",
      description:
        "Are you sure you want to delete this item? This action cannot be undone.",
      confirmText: "Yes, Delete",
      cancelText: "No, Cancel",
    });

    if (isConfirmed) {
      mutation.mutate(itemId);
    }
  }

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }

  if (isError) {
    toast.error(
      error.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
    );
    navigate("/inventory", { replace: true });
  }

  return (
    <div className="page item-details-page">
      <PageTitle text="Item Details" prev="/inventory">
        <div className="action-list-wide">
          <Link to={`/inventory/edit/${itemId}`}>
            <Button text="Edit Item Details" icon={<EditIcon />} />
          </Link>
          <Link to={`/inventory/stock/add?itemId=${itemId}`}>
            <Button text="Add New Stock" icon={<LibraryAddIcon />} />
          </Link>
          <IconButton icon={<DeleteIcon />} rounded onClick={handleDelete} />
        </div>

        <ListBox
          className="action-list"
          options={[
            {
              text: "Edit Item Details",
              icon: <EditIcon />,
              onClick: () => navigate(`/inventory/edit/${itemId}`),
            },
            {
              text: "Add New Stock",
              icon: <LibraryAddIcon />,
              onClick: () => navigate(`/inventory/stock/add?itemId=${itemId}`),
            },
            {
              text: "Delete Item",
              icon: <DeleteIcon />,
              onClick: handleDelete,
            },
          ]}
        />
      </PageTitle>

      <Section className="item-details">
        <div className="data-sec">
          <div className="left-bx">
            <div>
              <span>Item brand & name</span>
              <h2>
                <i className="item-brand-letter">
                  {item?.itemName?.charAt(0) || "I"}
                </i>
                {item?.brand} {item?.itemName}
              </h2>
            </div>

            <div>
              <span>Stock status</span>
              <p>{item?.inventory?.stockStatus}</p>
            </div>

            {/* LENSE SPECIFIC LEFT */}
            {item?.category === enums.ITEM_CATEGORIES.LENSE && (
              <>
                <div>
                  <span>Lense Type</span>
                  <p>{item?.attributes?.lense_type}</p>
                </div>
                <div className="d-row">
                  <div>
                    <span>SPH</span>
                    <p>{item?.attributes?.power_range?.sph}</p>
                  </div>
                  <div>
                    <span>CYL</span>
                    <p>{item?.attributes?.power_range?.cyl}</p>
                  </div>
                  <div>
                    <span>ADD</span>
                    <p>{item?.attributes?.power_range?.add}</p>
                  </div>
                </div>
                <div>
                  <span>Coating</span>
                  <p>{item?.attributes?.coating}</p>
                </div>
                {item?.attributes?.coating ===
                  enums.LENSE_COATINGS.PHOTO_GREY && (
                  <div>
                    <span>Transition</span>
                    <p>{item?.attributes?.transition}</p>
                  </div>
                )}
                <div>
                  <span>Material</span>
                  <p>{item?.attributes?.material}</p>
                </div>
                <div className="d-row">
                  <div>
                    <span>Thickness</span>
                    <p>{item?.attributes?.thickness}</p>
                  </div>
                  <div>
                    <span>Country</span>
                    <p>{item?.attributes?.manufacture_country}</p>
                  </div>
                </div>
              </>
            )}

            {/* FRAME SPECIFIC LEFT */}
            {item?.category === enums.ITEM_CATEGORIES.FRAME && (
              <>
                <div className="d-row">
                  <div>
                    <span>Lens diagonal</span>
                    <p>{item?.attributes?.size?.lens_diagonal}</p>
                  </div>
                  <div>
                    <span>Bridge</span>
                    <p>{item?.attributes?.size?.bridge}</p>
                  </div>
                  <div>
                    <span>Arm length</span>
                    <p>{item?.attributes?.size?.arm_length}</p>
                  </div>
                </div>
                <div>
                  <span>Material</span>
                  <p>{item?.attributes?.material}</p>
                </div>
              </>
            )}

            {/* SUNGLASS SPECIFIC LEFT */}
            {item?.category === enums.ITEM_CATEGORIES.SUNGLASS && (
              <>
                <div className="d-row">
                  <div>
                    <span>Lens diagonal</span>
                    <p>{item?.attributes?.size?.lens_diagonal}</p>
                  </div>
                  <div>
                    <span>Bridge</span>
                    <p>{item?.attributes?.size?.bridge}</p>
                  </div>
                  <div>
                    <span>Arm length</span>
                    <p>{item?.attributes?.size?.arm_length}</p>
                  </div>
                </div>
                <div className="d-row">
                  <div>
                    <span>Frame Material</span>
                    <p>{item?.attributes?.frame_material}</p>
                  </div>
                  <div>
                    <span>Frame Color</span>
                    <p>{item?.attributes?.frame_color}</p>
                  </div>
                </div>
                <div className="d-row">
                  <div>
                    <span>Lens Material</span>
                    <p>{item?.attributes?.lens_material}</p>
                  </div>
                  <div>
                    <span>Lens Color</span>
                    <p>{item?.attributes?.lens_color}</p>
                  </div>
                </div>
              </>
            )}

            {/* ACCESSORIES SPECIFIC LEFT */}
            {item?.category === enums.ITEM_CATEGORIES.ACCESSORIES && (
              <>
                <div>
                  <span>Type</span>
                  <p>{item?.attributes?.type}</p>
                </div>
                <div>
                  <span>Material</span>
                  <p>{item?.attributes?.material}</p>
                </div>
                <div>
                  <span>Color</span>
                  <p>{item?.attributes?.color}</p>
                </div>
              </>
            )}
          </div>

          <div className="right-bx">
            <div>
              <span>Item type</span>
              <div>
                <Badge
                  text={item?.category}
                  outlined
                  variant={makeBadgeVariant(item?.category)}
                />
              </div>
            </div>

            <div>
              <span>Warranty period (Months)</span>
              <p>{item?.warrantyPeriodMonths}</p>
            </div>

            {/* FRAME & SUNGLASS MODEL NUMBER */}
            {(item?.category === enums.ITEM_CATEGORIES.FRAME ||
              item?.category === enums.ITEM_CATEGORIES.SUNGLASS) && (
              <div>
                <span>Model number</span>
                <p>{item?.attributes?.model_number}</p>
              </div>
            )}

            {/* SUNGLASS SPECIFIC RIGHT */}
            {item?.category === enums.ITEM_CATEGORIES.SUNGLASS && (
              <>
                <div>
                  <span>Shape</span>
                  <p>{item?.attributes?.shape}</p>
                </div>
                <div className="d-row">
                  <div>
                    <span>Lens Type</span>
                    <p>{item?.attributes?.lens_type}</p>
                  </div>
                  <div>
                    <span>UV Protection</span>
                    <p>{item?.attributes?.uv_protection ? "Yes" : "No"}</p>
                  </div>
                </div>
                <div>
                  <span>Gender</span>
                  <p>{item?.attributes?.gender}</p>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="bot-box">
          <DataRow
            labelLeft="Purchase Price"
            dataLeft={
              <div className="bot-box-item-detail-wrapper">
                {currencyFormatter(item?.purchasePrice)}
                <small> LKR</small>
              </div>
            }
            labelRight="Selling Price"
            dataRight={
              <div className="bot-box-item-detail-wrapper">
                {currencyFormatter(item?.sellingPrice)}
                <small> LKR</small>
              </div>
            }
          />
          {item?.inventory?.stockStatus === enums.STOCK_STATUS.READY ? (
            <DataRow
              labelLeft="Storeroom Stock"
              dataLeft={
                <div className="bot-box-item-detail-wrapper">
                  {item?.inventory?.storeRoomStock}
                </div>
              }
              labelRight="Showroom Stock"
              dataRight={
                <div className="bot-box-item-detail-wrapper">
                  {item?.inventory?.displayStock}
                </div>
              }
            />
          ) : null}
        </div>
      </Section>
    </div>
  );
}

export default ItemDetailsPage;
